import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { Component, Inject, OnInit, Optional } from '@angular/core';
import { Observable, firstValueFrom, of } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ValidationPipe } from '../../core/pipes/validation.pipe';
import { AuthService } from 'libs/pnp-services/src/lib/auth/auth.service';
import { NotificationsService, PnpUserService } from '@pn-plus/pnp-services';
import { ITokenInfo } from 'libs/pnp-services/src/lib/api/_models/pnp-token-info.model';
import { IUserPasswordResetConfirmationPayload } from 'libs/pnp-services/src/lib/api/user/pnp-user.model';
import { IAuthModel } from 'libs/pnp-services/src/lib/auth/_models/auth.model';
import { LoginService } from '../../core/services/login.service';

@Component({
    selector: 'set-password',
    templateUrl: './set-password.component.html',
    styleUrls: ['./set-password.component.scss'],
    providers: [ValidationPipe],
})
export class SetPasswordComponent implements OnInit {
    error: string;
    showPassword = false;
    passwordForm: FormGroup;
    isLoading = false;

    validationFields = [
        {
            title: 'at least 8 characters',
            regularExp: /^.{8,}$/,
        },
        {
            title: 'one upper-case letter (A-Z)',
            regularExp: /.*[A-Z].*/,
        },
        {
            title: 'one lower-case letter (a-z)',
            regularExp: /.*[a-z].*/,
        },
        {
            title: 'one digit (0-9)',
            regularExp: /.*\d.*/,
        },
        {
            title: 'one special character (~!@#$%^&*)',
            regularExp: /.*[~!@#$%^&*].*/,
        },
    ];

    constructor(
        @Optional() @Inject(MAT_DIALOG_DATA) public data: ITokenInfo,
        @Optional() public dialogRef: MatDialogRef<SetPasswordComponent>,
        private formBuilder: FormBuilder,
        private router: Router,
        private validationPipe: ValidationPipe,
        private loginService: LoginService,
        private authService: AuthService,
        private pnpUserService: PnpUserService,
        private notificationService: NotificationsService,
    ) {
        const loginResponse = this.authService.getFormDataLoginResponse();
        if (loginResponse !== null) {
            this.data = loginResponse;
        }
    }

    ngOnInit(): void {
        this.passwordForm = this.formBuilder.group(
            {
                newPassword: [
                    '',
                    [
                        Validators.required,
                        this.newPasswordRegExpValidator(this.validationFields),
                    ],
                ],
                confirmPassword: [''],
            },
            {
                validator: this.passwordMatchValidator(
                    'newPassword',
                    'confirmPassword',
                ),
            },
        );
    }

    toggleShowPassword() {
        this.showPassword = !this.showPassword;
    }

    async onSubmit() {
        this.setBusy(true);
        let authModel = this.authService.getAuthModel();
        if (!authModel) {
            authModel = {
                loginPayload: <any>null,
                loginResponse: <any>null,
                passwordPayload: <any>null,
            };
        }
        const password = this.passwordForm.value.newPassword;
        authModel.passwordPayload = { newPassword: password };

        if (!this.data) {
            authModel = await firstValueFrom(this.updatePassword(authModel));
            authModel = await firstValueFrom(this.updateHasPassword(authModel));
            if (!authModel.error) {
                this.updateRegistration();
            }
        } else {
            const model: IUserPasswordResetConfirmationPayload = {
                passwordResetConfirmationToken: this.data.jwt,
                newPassword: this.passwordForm.value.newPassword,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            };

            const result = await firstValueFrom(this.setNewPassword(model));
        }

        if (authModel.error) {
            this.error = authModel.error;
            return;
        }

        if (!this.data) {
            this.loginService.navigate('/set-pin');
        } else {
            const message = 'Password reset successfully. Please log in using your new password.';
            this.notificationService.showSuccessNotification(message);
            this.loginService.setFormDataRecoveryResponse(null);
            this.loginService.setFormDataLoginResponse(null);
            this.loginService.navigate('/');
        }

        this.setBusy(false);
    }

    updateRegistration() {
        const authModel = this.authService.getAuthModel();
        if (authModel.registrationResponse) {
            authModel.registrationResponse.hasPassword = true;
        }
        this.authService.updateAuthModel(authModel);
        return authModel;
    }

    setNewPassword(
        newPasswordModel: IUserPasswordResetConfirmationPayload,
    ): Observable<any> {
        let error = null;
        return this.pnpUserService
            .putUserPasswordResetConfirmation(newPasswordModel)
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    error = errorResponse.error;
                    return of(error);
                }),
                map((response) => {
                    return response;
                }),
            );
    }

    updatePassword(authModel: IAuthModel): Observable<IAuthModel> {
        if (authModel.error) {
            return of(authModel);
        }

        return this.pnpUserService
            .putPassword(<any>authModel.passwordPayload)
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    authModel.error = errorResponse.error;
                    return of(authModel);
                }),
                map((response) => {
                    if (!authModel.error) {
                        authModel.phoneNumberResponse = <any>response;
                    }
                    return authModel;
                }),
            );
    }

    updateHasPassword(authModel: IAuthModel): Observable<IAuthModel> {
        if (authModel.error) {
            return of(authModel);
        }

        return this.pnpUserService.putHasPassword().pipe(
            catchError((errorResponse: HttpErrorResponse) => {
                authModel.error = errorResponse.error;
                return of(authModel);
            }),
            map(() => {
                return authModel;
            }),
        );
    }

    passwordMatchValidator(newPassword: string, confirmPassword: string) {
        return (formGroup: FormGroup) => {
            const control = formGroup.controls[newPassword];
            const confirmControl = formGroup.controls[confirmPassword];
            if (control.value !== confirmControl.value) {
                confirmControl.setErrors({ mustMatch: true });
            } else {
                confirmControl.setErrors(null);
            }
        };
    }

    newPasswordRegExpValidator(
        valFields: {
            title: string;
            regularExp: RegExp;
        }[],
    ): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            if (!value) {
                return null;
            }
            const isPasswordValid = valFields.every((item) =>
                this.validationPipe.transform(value, item.regularExp),
            );
            return !isPasswordValid ? { invalid: true } : null;
        };
    }

    setBusy(flag: boolean): void {
        this.isLoading = flag;
    }
}
